import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { TopBar } from "../components/layout/TopBar";
import { Spinner } from "../components/shared/Spinner";
import { StatusBadge } from "../components/shared/StatusBadge";
import { EmptyState } from "../components/shared/EmptyState";
import * as ui from "../styles/ui";
import {
  api,
  type DocumentFamily,
  type DocumentVersion,
  type GitDocEntry,
} from "../api/client";
import { useGitStore } from "../stores/useGitStore";
import { formatApiError } from "../lib/api-errors";
import type { DocStatus } from "../../shared/types";
import { versionSortKey } from "../../shared/versions";

export type LiveMatchState = "aligned" | "drift" | "unpublished" | "no-git";

export function liveMatchState(
  gitVersion: string | null,
  liveVersion: string | null,
): LiveMatchState {
  if (!gitVersion && !liveVersion) return "no-git";
  if (gitVersion && !liveVersion) return "unpublished";
  if (!gitVersion && liveVersion) return "drift";
  return gitVersion === liveVersion ? "aligned" : "drift";
}

interface LiveRow {
  familySlug: string;
  familyPath: string | null;
  title: string;
  docType: string;
  gitStatus: string | null;
  gitVersion: string | null;
  liveVersion: string | null;
  liveStatus: string | null;
  pdfUrl: string | null;
  familyId: string;
}

function slugOf(f: DocumentFamily): string {
  if (typeof f.slug === "string") return f.slug;
  return f.slug?.current ?? "";
}

export function LivePage() {
  const navigate = useNavigate();
  const fetchTree = useGitStore((s) => s.fetchTree);
  const [rows, setRows] = useState<LiveRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api.sanity.families().catch(() => [] as DocumentFamily[]),
      fetchTree(true),
    ])
      .then(async ([families, gitDocs]) => {
        if (cancelled) return;

        const gitBySlug = new Map<string, GitDocEntry[]>();
        for (const doc of gitDocs) {
          const short = doc.family.split("/").pop() ?? doc.family;
          const list = gitBySlug.get(short) ?? [];
          list.push(doc);
          gitBySlug.set(short, list);
        }

        const liveByFamily = await Promise.all(
          families.map((fam) =>
            api.sanity
              .versions(fam.id)
              .then(
                (versions) =>
                  versions.find((v) => v.status === "published") ??
                  versions[0] ??
                  null,
              )
              .catch(() => null as DocumentVersion | null),
          ),
        );
        if (cancelled) return;

        const next: LiveRow[] = [];
        families.forEach((fam, i) => {
          const slug = slugOf(fam);
          const gitVersions =
            gitBySlug.get(slug) ?? gitBySlug.get(fam.title) ?? [];
          const latestGit = gitVersions
            .sort(
              (a, b) => versionSortKey(a.version) - versionSortKey(b.version),
            )
            .at(-1);

          const live = liveByFamily[i];

          const liveVer =
            typeof fam.currentVersion === "object" && fam.currentVersion
              ? fam.currentVersion.versionString
              : typeof fam.currentVersion === "string"
                ? fam.currentVersion
                : (live?.versionString ?? null);

          next.push({
            familySlug: slug,
            familyPath: latestGit?.family ?? null,
            title: fam.title,
            docType: fam.docType,
            gitStatus: latestGit?.status ?? null,
            gitVersion: latestGit?.version ?? null,
            liveVersion: liveVer ?? live?.versionString ?? null,
            liveStatus: live?.status ?? null,
            pdfUrl: live?.pdfUrl ?? null,
            familyId: fam.id,
          });
        });

        for (const [slug, docs] of gitBySlug) {
          if (next.some((r) => r.familySlug === slug)) continue;
          const latest = docs
            .slice()
            .sort(
              (a, b) => versionSortKey(a.version) - versionSortKey(b.version),
            )
            .at(-1)!;
          next.push({
            familySlug: slug,
            familyPath: latest.family,
            title: latest.title,
            docType: latest.docType,
            gitStatus: latest.status,
            gitVersion: latest.version,
            liveVersion: null,
            liveStatus: null,
            pdfUrl: null,
            familyId: "",
          });
        }

        setRows(next.sort((a, b) => a.title.localeCompare(b.title)));
      })
      .catch((e) => {
        if (!cancelled) setError(formatApiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchTree]);

  return (
    <>
      <TopBar title="live" />
      <div style={ui.pageContent}>
        {loading && <Spinner size={24} label="loading…" />}

        {error && <p style={styles.error}>{error}</p>}

        {!loading && !error && rows.length === 0 && (
          <EmptyState
            title="nothing to compare"
            body="publish a document first"
            action={{ label: "dashboard", onClick: () => navigate("/") }}
          />
        )}

        {!loading && rows.length > 0 && (
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={ui.th}>family</th>
                <th style={ui.th}>git (latest)</th>
                <th style={ui.th}>live (sanity)</th>
                <th style={ui.th}>match</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const matchState = liveMatchState(
                  row.gitVersion,
                  row.liveVersion,
                );
                const matchColor =
                  matchState === "aligned"
                    ? "var(--comp-green)"
                    : matchState === "no-git"
                      ? "var(--fg-3)"
                      : matchState === "unpublished"
                        ? "var(--fg-3)"
                        : "var(--comp-mustard)";
                const matchLabel = matchState === "no-git" ? "—" : matchState;
                return (
                  <tr key={row.familySlug} style={ui.row}>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() =>
                          navigate(
                            `/family/${encodeURIComponent(row.familyPath ?? row.familySlug)}`,
                          )
                        }
                      >
                        {row.title}
                      </button>
                      <div style={styles.sub}>{row.docType}</div>
                    </td>
                    <td style={styles.td}>
                      {row.gitVersion ? (
                        <>
                          <span style={styles.mono}>{row.gitVersion}</span>
                          {row.gitStatus && (
                            <StatusBadge status={row.gitStatus as DocStatus} />
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={styles.td}>
                      {row.liveVersion ? (
                        <>
                          <span style={styles.mono}>{row.liveVersion}</span>
                          {row.pdfUrl && (
                            <a
                              href={row.pdfUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={styles.pdfLink}
                            >
                              pdf
                            </a>
                          )}
                        </>
                      ) : (
                        <span style={styles.muted}>not published</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={{ color: matchColor }}>{matchLabel}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  error: { color: "var(--comp-salmon)", fontSize: 13 },
  td: { ...ui.td, verticalAlign: "top" },
  link: {
    background: "none",
    border: "none",
    padding: 0,
    fontWeight: 600,
    color: "var(--fg-1)",
    cursor: "pointer",
    textAlign: "left",
  },
  sub: { ...ui.metaText, marginTop: 2 },
  mono: { fontFamily: "var(--font-mono)", fontSize: 12, marginRight: 8 },
  pdfLink: { fontSize: 11, marginLeft: 8 },
  muted: { color: "var(--fg-3)", fontSize: 12 },
};
